import { useEffect, useRef, useState } from 'preact/hooks'
import { integrationsApi } from '@/api/integrations'

export interface ErrorSummaryState {
  aiSummary: string | null
  loading: boolean
  failed: boolean
}

/**
 * Resolves the AI error summary for an integration connection.
 *
 * - If the list/status response already carried an `aiSummary` (server cache hit), render
 *   it directly via the `seed` and skip the round-trip.
 * - Otherwise, when both ids are present, auto-fire on mount to fetch-or-generate.
 * - When ids are missing (e.g. HubSpot's status DTO surfaces `aiSummary` but no connection
 *   id), the seed still renders; we simply never fetch.
 *
 * Callers only mount this where errors exist, so healthy integrations never trigger a call.
 */
export function useErrorSummary(
  clientCompanyId?: string | null,
  connectionId?: string,
  seed?: string | null,
  onFetched?: (summary: string) => void,
): ErrorSummaryState {
  const [aiSummary, setAiSummary] = useState<string | null>(seed ?? null)
  // Start loading-truthy when we have ids but no seed, so the first paint renders the
  // skeleton instead of flashing the failure fallback in the tick before the useEffect
  // below sets this itself.
  const [loading, setLoading] = useState<boolean>(
    !seed && !!clientCompanyId && !!connectionId
  )
  const [failed, setFailed] = useState(false)

  // Stash the callback in a ref so its identity doesn't force the fetch effect to re-run.
  // The effect only cares about ids and the seed.
  const onFetchedRef = useRef(onFetched)
  useEffect(() => { onFetchedRef.current = onFetched }, [onFetched])

  useEffect(() => {
    if (seed) {
      setAiSummary(seed)
      setLoading(false)
      setFailed(false)
      return
    }
    // Without both ids there's nothing to fetch — leave state idle so the section stays
    // empty rather than spinning forever.
    if (!clientCompanyId || !connectionId) {
      setAiSummary(null)
      setLoading(false)
      setFailed(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setFailed(false)
    setAiSummary(null)
    integrationsApi.getIntegrationErrorSummary(clientCompanyId, connectionId)
      .then(res => {
        if (cancelled) return
        if (res.data?.aiSummary) {
          setAiSummary(res.data.aiSummary)
          // Hand the summary back to the list-owner so subsequent opens of the same card
          // short-circuit on the seeded value — no skeleton flash.
          onFetchedRef.current?.(res.data.aiSummary)
        } else {
          setFailed(true)
        }
      })
      .catch(() => { if (!cancelled) setFailed(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [clientCompanyId, connectionId, seed])

  return { aiSummary, loading, failed }
}
